// Admin → Schools. Read-only list of every school with counts + subscription.
// Click through to the drill-in. docs/replan-v2.md (admin console, slice 2).
// Migrated to the console component system in M7c / P6.
//
// ⚠️ Schools are DARK for the MVP (families-only, owner 2026-07-19) but must
// keep compiling and rendering — Phase 2 turns them on additively. This
// migration changes presentation only; no behaviour, no gating.

import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';

import {
  DataTable,
  FilterBar,
  PageHeader,
  StatusBadge,
  actionsColumn,
  sortRows,
  useTableState,
  type Column,
} from '~/components';
import { listSchools } from '~/lib/admin';

export const Route = createFileRoute('/_authed/schools/')({
  component: SchoolsPage,
});

type SchoolRow = Awaited<ReturnType<typeof listSchools>>[number];

function SchoolsPage() {
  const navigate = useNavigate();
  const table = useTableState({ defaultSort: { key: 'name', direction: 'asc' } });

  const schools = useQuery({
    queryKey: ['schools'],
    queryFn: ({ signal }) => listSchools(signal),
  });

  const rows = useMemo(() => {
    const all = schools.data ?? [];
    const needle = table.q.trim().toLowerCase();
    const filtered = needle
      ? all.filter(
          (s) =>
            s.name.toLowerCase().includes(needle) ||
            s.slug.toLowerCase().includes(needle) ||
            (s.country ?? '').toLowerCase().includes(needle),
        )
      : all;
    return sortRows(filtered, table.sort, {
      name: (s) => s.name,
      teacherCount: (s) => s.teacherCount,
      studentCount: (s) => s.studentCount,
      classCount: (s) => s.classCount,
      subscriptionStatus: (s) => s.subscriptionStatus,
    });
  }, [schools.data, table.q, table.sort]);

  const columns: Column<SchoolRow>[] = [
    {
      key: 'name',
      header: 'School',
      sortable: true,
      alwaysVisible: true,
      render: (s) => (
        <div className="min-w-0">
          <Link
            to="/schools/$id"
            params={{ id: s.id }}
            onClick={(e) => e.stopPropagation()}
            className="truncate font-medium text-admin-900 hover:underline"
          >
            {s.name}
          </Link>
          <p className="mt-0.5 truncate text-xs text-admin-500">
            {s.country} · {s.slug}
          </p>
        </div>
      ),
    },
    {
      key: 'teacherCount',
      header: 'Teachers',
      sortable: true,
      align: 'end',
      render: (s) => <span className="text-admin-700">{s.teacherCount}</span>,
    },
    {
      key: 'studentCount',
      header: 'Students',
      sortable: true,
      align: 'end',
      render: (s) => <span className="text-admin-700">{s.studentCount}</span>,
    },
    {
      key: 'classCount',
      header: 'Classes',
      sortable: true,
      align: 'end',
      hideBelow: 'md',
      render: (s) => <span className="text-admin-700">{s.classCount}</span>,
    },
    {
      key: 'subscriptionStatus',
      header: 'Subscription',
      sortable: true,
      render: (s) =>
        s.subscriptionStatus ? (
          <StatusBadge status={s.subscriptionStatus} dot />
        ) : (
          <span className="text-admin-300">—</span>
        ),
    },
    actionsColumn<SchoolRow>({
      label: (s) => s.name,
      primary: (s) => ({
        id: 'open',
        label: 'Open',
        icon: 'edit',
        onSelect: () => void navigate({ to: '/schools/$id', params: { id: s.id } }),
      }),
      actions: (s) => [
        {
          id: 'invoices',
          label: 'View invoices',
          icon: 'receipt',
          onSelect: () => window.location.assign(`/payments?q=${encodeURIComponent(s.name)}`),
        },
        {
          id: 'copy-slug',
          label: 'Copy slug',
          icon: 'copy',
          onSelect: () => void navigator.clipboard?.writeText(s.slug),
        },
      ],
    }),
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Schools"
        description="Every school tenant — teachers, classes, students."
      />

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(s) => s.id}
        loading={schools.isLoading}
        error={schools.error}
        onRetry={() => void schools.refetch()}
        sort={table.sort}
        onSortChange={table.setSort}
        onRowClick={(s) => void navigate({ to: '/schools/$id', params: { id: s.id } })}
        columnVisibility
        caption="Schools"
        empty={{
          icon: 'school',
          title: table.isFiltered ? 'No schools match' : 'No schools yet',
          // Honest about the MVP scope rather than implying something is broken.
          description: table.isFiltered
            ? 'Try clearing the search.'
            : 'School accounts are a Phase 2 feature — nothing is wrong.',
        }}
        toolbar={
          <FilterBar
            query={table.q}
            onQueryChange={table.setQuery}
            searchPlaceholder="Search schools…"
            onClear={table.clearFilters}
            isFiltered={table.isFiltered}
          />
        }
      />
    </div>
  );
}
